import { useMemo } from 'react';

import { type HomeProductCategory } from '../useHomeContent';
import { useHomeMenu } from '../useHomeMenu';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Removes products from the filterable and generic categories that are not in the products array.
 * If there are no products, returns only the generic categories.
 */
export const useHomeFiltering = (params: UseHomeFilteringParams): UseHomeFilteringResult => {
  const { products = [], filterableCategories, genericCategories } = params;

  return useMemo(() => {
    if (products.length === 0) {
      return {
        filterableCategories: [],
        genericCategories,
      };
    }

    const menuProductsByLineageKey = getMenuProductsByLineageKey(products);

    return {
      filterableCategories: filterCategories(filterableCategories, menuProductsByLineageKey),
      genericCategories: filterCategories(genericCategories, menuProductsByLineageKey),
    };
  }, [products, filterableCategories, genericCategories]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Removes categories that have no products.
 */
function filterCategories(
  categories: HomeProductCategory[],
  menuProductsByLineageKey: MenuProductsByLineageKey,
) {
  return categories
    .map((category) => filterCategoryProducts(category, menuProductsByLineageKey))
    .filter((category) => (category.products?.length ?? 0) > 0);
}

/**
 * Removes products that are not in the selected menu and enriches them with the menu data.
 */
function filterCategoryProducts(
  category: HomeProductCategory,
  menuProductsByLineageKey: MenuProductsByLineageKey,
) {
  return {
    ...category,
    products: category.products
      ?.filter((product) => menuProductsByLineageKey.has(product.lineageKey))
      ?.map((product) => {
        const menuProduct = menuProductsByLineageKey.get(product.lineageKey)!;

        return {
          ...product,
          id: menuProduct.id,
          slug: menuProduct.slug,
          isModifiable: menuProduct.isModifiable,
          cost: menuProduct.cost,
          allergenProperties: menuProduct.allergenProperties,
          calories: menuProduct.calories ?? 0,
          totalFatG: menuProduct.totalFatG ?? 0,
          totalCarbsG: menuProduct.totalCarbsG ?? 0,
          dietaryFiberG: menuProduct.dietaryFiberG ?? 0,
          sugarsG: menuProduct.sugarsG ?? 0,
          proteinG: menuProduct.proteinG ?? 0,
          co2eKg: menuProduct.co2eKg ?? 0,
        };
      }),
  };
}

/**
 * Map of products by lineage key, to be provided in the Contentful content.
 */
function getMenuProductsByLineageKey(products: NonNullable<UseHomeFilteringParams['products']>) {
  return new Map(
    products
      .filter((product): product is MenuProductWithLineageKey => Boolean(product.lineageKey))
      .map((product) => [product.lineageKey, product]),
  );
}

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuProduct = NonNullable<ReturnType<typeof useHomeMenu>['products']>[number];

type MenuProductWithLineageKey = MenuProduct & { slug: string; lineageKey: string };

type MenuProductsByLineageKey = Map<string, MenuProductWithLineageKey>;

type UseHomeFilteringParams = {
  products: ReturnType<typeof useHomeMenu>['products'];
  filterableCategories: HomeProductCategory[];
  genericCategories: HomeProductCategory[];
};

type UseHomeFilteringResult = {
  filterableCategories: HomeProductCategory[];
  genericCategories: HomeProductCategory[];
};
